import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { api, apiErrorMessage } from "../../lib/api";
import {
  REMINDER_KINDS, REMINDER_STATES, canSendNow, reminderWhen, remindersHeadline,
} from "../../lib/sessionReminders";

/**
 * What each session's reminders did, and a way to send one now (23.09.2026).
 * Reading a mail log is not an answer to "did they get the link?".
 */
export default function GroupSessionReminders({ groupId, canMutate, currentRole }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/groups/${groupId}/session-reminders`);
      setData(r.data);
    } catch (err) {
      setData({ error: apiErrorMessage(err, "Nu am putut citi reamintirile.") });
    }
  }, [groupId]);

  useEffect(() => { load(); }, [load]);

  const sendNow = async (session, kind) => {
    if (!window.confirm(`Trimit acum reamintirea ${REMINDER_KINDS[kind].label} pentru ${session.label}? `
      + "Cine a primit-o deja nu o primește din nou.")) return;
    setBusy(`${session.key}-${kind}`);
    try {
      const r = await api.post(`/admin/groups/${groupId}/session-reminders/send`, null,
        { params: { session_key: session.key, kind } });
      toast.success(r.data.message);
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Trimiterea nu a reușit."));
    } finally {
      setBusy(null);
    }
  };

  if (!data) return <p className="text-xs text-[#6B7280]">Se încarcă reamintirile…</p>;
  if (data.error) return <p className="text-xs text-[#9b1f2c]">{data.error}</p>;

  const head = remindersHeadline(data);
  const tone = { ok: "bg-[#F5F6F6] text-[#373A45]", bad: "bg-[#FDECEC] text-[#9b1f2c]",
    off: "bg-[#EDEEF0] text-[#6B7280]" }[head.tone];

  return (
    <div className="rounded-xl border border-[#E5E7EB] bg-white p-4" data-testid="group-session-reminders">
      <div className="text-sm font-semibold text-[#373A45]">Reamintiri înainte de sesiuni</div>
      <p className={`mt-2 rounded-lg px-3 py-2 text-xs ${tone}`} data-testid="group-reminders-headline">{head.text}</p>

      {data.sessions.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-[11px]">
            <thead>
              <tr className="text-left text-[#6B7280]">
                <th className="py-1 pr-3">Sesiune</th>
                <th className="py-1 pr-3">Reamintire</th>
                <th className="py-1 pr-3">Stare</th>
                <th className="py-1">Trimise</th>
              </tr>
            </thead>
            <tbody>
              {data.sessions.map((s) => Object.entries(s.kinds).map(([kind, k]) => {
                const state = REMINDER_STATES[k.state] || REMINDER_STATES.waiting;
                const key = `${s.key}-${kind}`;
                return (
                  <tr key={key} className="border-t border-[#E5E7EB]" data-testid={`reminder-row-${key}`}>
                    <td className="py-1.5 pr-3">
                      <span className={s.past ? "text-[#6B7280]" : "font-medium text-[#373A45]"}>
                        {s.index}. {s.label}
                      </span>
                      {s.own_link && <span className="ml-1 text-[#6B7280]">· link propriu</span>}
                    </td>
                    <td className="py-1.5 pr-3 text-[#6B7280]">
                      {REMINDER_KINDS[kind].label}
                      {reminderWhen(k) && (
                        <span className="block text-[11px] text-[#9AA0A6]" data-testid={`reminder-when-${key}`}>
                          {k.state === "sent" ? "era programată " : ""}{reminderWhen(k)}
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 pr-3">
                      <span className={`rounded-full px-2 py-0.5 font-semibold ${state.cls}`}>{state.label}</span>
                    </td>
                    <td className="py-1.5">
                      <span className="tabular-nums text-[#373A45]">{k.sent}</span>
                      {canSendNow(s, k, { canMutate, role: currentRole }) && (
                        <button type="button" onClick={() => sendNow(s, kind)} disabled={Boolean(busy)}
                          className="ml-2 inline-flex items-center gap-1 rounded-full border border-[#E5E7EB] px-2 py-0.5 font-semibold hover:bg-[#F5F6F6] disabled:opacity-50"
                          data-testid={`reminder-send-${key}`}>
                          {busy === key ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} />}
                          Trimite acum
                        </button>
                      )}
                    </td>
                  </tr>
                );
              }))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
